import pako from "pako";

export function hasKeys(obj: Object) {
  return Boolean(Object.keys(obj).length);
}

export function objToFormData(
  obj: Record<string, any>,
  keysToRemove: string[] = [],
) {
  const formData = new FormData();
  for (const key in obj) {
    const isClearedValue = obj[key] === "" || obj[key] === null;
    if ((obj[key] || isClearedValue) && !keysToRemove.includes(key))
      formData.append(key, obj[key] ?? "");
  }

  return formData;
}

// Simplified deep clone for objects only
export function deepClone<T>(
  obj: T,
  hash: WeakMap<object, any> = new WeakMap(),
): T {
  // Handle null and undefined
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle primitive types
  if (typeof obj !== "object") {
    return obj;
  }

  // Handle circular references
  if (hash.has(obj as object)) {
    return hash.get(obj as object);
  }

  // Handle Arrays
  if (Array.isArray(obj)) {
    const clonedArray: any[] = [];
    hash.set(obj as object, clonedArray);

    for (let i = 0; i < obj.length; i++) {
      clonedArray[i] = deepClone(obj[i], hash);
    }
    return clonedArray as T;
  }

  // Handle plain objects
  const clonedObj: any = {};
  hash.set(obj as object, clonedObj);

  // Clone all enumerable properties
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      clonedObj[key] = deepClone((obj as any)[key], hash);
    }
  }

  return clonedObj as T;
}

// Helper to compress/decompress
export function compressObject<T>(obj: T): string {
  const json = JSON.stringify(obj ?? {});
  const compressed = pako.deflate(json);
  const base64 = btoa(
    String.fromCharCode.apply(null, compressed as unknown as number[]),
  );
  return base64;
}

export function decompressObject<T>(compressed: string): T | undefined {
  try {
    const binary = atob(compressed);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const decompressed = pako.inflate(bytes);
    return JSON.parse(new TextDecoder().decode(decompressed));
  } catch {
    return undefined;
  }
}
